import type {ReactElement} from 'react';
import React from 'react';
import {PosterNavOpts} from '@Components/poster-editor/components/poster-nav-opts';
import {PosterEditorNavBar} from '@Components/poster-editor/components/poster-editor-nav-bar';
import {AudioPlaylistItem} from '@Components/poster-editor/components/audio-playlist-item';
import type {PosterOpts} from '@PosterWhiteboard/poster/poster.class';
import {PosterModeType} from '@PosterWhiteboard/poster/poster-mode.class';
import {SaveReminderNotification} from '@Components/save-reminder-notification';
import type {PosterEditorConfig} from '@Components/poster-editor/poster-editor.types';
import {isScaleAnimatingToFitToScreen} from '@Components/poster-editor/poster-editor-reducer';
import {PosterWhiteboard} from '@Components/poster-whiteboard';
import {PosterMobileSmallSeekbar} from '@Components/poster-editor/components/poster-mobile-small-seekbar';
import {POSTER_MOBILE_BIG_SEEK_BAR_HEIGHT} from '@Components/poster-editor/components/poster-mobile-big-seekbar/poster-mobile-big-seekbar.types';
import {PosterEditorBottomBar} from '@Components/poster-editor/components/poster-editor-bottom-bar';
import {isEditorMobileVariant} from '@Components/poster-editor/library/poster-editor-library';
import {PostPosterElement} from '@Components/poster-editor/components/post-poster-element';
import {PostPosterModalSpacer} from '@Components/poster-editor/components/post-poster-modal-spacer';
import {LoadingToast} from '@Components/loading-toast';
import {MessageGrowl} from '@Components/message-growl';
import {Snackbar} from '@Components/snackbar';
import {SnackbarMessageGrowl} from '@Components/snackbar-message-growl';
import {usePosterEditorPrefetchModules} from '@Hooks/poster-editor/usePosterEditorPrefetchModules';
import {CanvasZoomPinchOnboarding} from '@Components/poster-whiteboard/components/canvas-zoom-pinch-onboarding';
import {PosterEditorWebBottomBar} from '@Components/poster-editor/components/poster-editor-web-bottom-bar';
import {PosterEditingNavbarMode} from '@Components/poster-editor/components/poster-editor-nav-bar/poster-editor-nav-bar.types';
import {PosterEditorErrorBoundary} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editor-error-boundary';
import styles from './poster-editor.module.scss';
import {PosterAddItemBar} from './components/poster-add-item-bar';
import {PosterEditingSidePanel} from './components/poster-editing-side-panel';
import {useAppSelector} from '@/hooks';
// needed in download-publish-dialog.js.
import '@ThirdParty/modernizr.custom.24530';
import '@Global/download-publish-dialog';
import '@Global/message-growl';
// needed in download-publish-dialog.js to open download modal.
import '@Global/modal';
// needed in download-publish-dialog.js.
import '@Global/loading-toast';
import {CursorOverlay} from '@Components/poster-editor/components/cursor-overlay';
import {InitalPosterItemsLoadErrorNotification} from '../inital-poster-items-load-error/inital-poster-items-load-error-notification';
import {TimelineMobileOnboarding} from '@Components/poster-editor/components/poster-editor-bottom-bar/components/timeline-mobile-onboarding/timeline-mobile-onboarding';
import {useIsVideoOrHasMultiPages} from '@Hooks/poster-editor/useIsVideoOrHasMultiPages';
import {useInitPosterEditor} from '@Hooks/poster-editor/useInitPosterEditor';
import {usePosterPushModalEffects} from '@Hooks/poster-editor/usePosterPushModalEffects';
import {useToggleNotificationOnAISubtitlesGenerating} from '@Hooks/poster-editor/useToggleNotificationOnAISubtitlesGenerating';
import {useEditorMobileOnboarding} from '@Hooks/poster-editor/useEditorMobileOnboarding';
import {useHandleEditorSidebarCollapsibleState} from '@Hooks/poster-editor/useHandleEditorSidebarCollapsibleState';

window.isPosterMaker = true;
window.posterEditor = {
  whiteboard: undefined,
  config: undefined,
  elements: {},
};

export interface PosterEditorProps {
  className?: string;
  posterOpts: PosterOpts;
  posterEditorConfig: PosterEditorConfig;
  isEmbedded?: boolean;
}

export function PosterEditor({className = '', isEmbedded = false, ...props}: PosterEditorProps): ReactElement {
  const isMobileVariant =
    useAppSelector((state) => {
      return state.posterEditor.isMobileVariant;
    }) ?? false;
  const isScaleAnimatingToFit = useAppSelector(isScaleAnimatingToFitToScreen);
  const isPosterVideo = useIsVideoOrHasMultiPages();
  const canvasHeight = useAppSelector((state) => {
    return state.posterEditor.canvasDimensions.height;
  });
  const whiteboardContainerHeight = useAppSelector((state) => {
    return state.posterEditor.whiteboardContainerDimensions.height;
  });
  const isSidebarSmall = useAppSelector((state) => {
    return state.posterEditor.isSidebarSmall;
  });

  usePosterEditorPrefetchModules();
  useInitPosterEditor({posterEditorConfig: props.posterEditorConfig});
  usePosterPushModalEffects();
  useToggleNotificationOnAISubtitlesGenerating();
  useEditorMobileOnboarding({
    showOnboardingFromConfig: props.posterEditorConfig.showMobileUXOnBoarding,
  });
  useHandleEditorSidebarCollapsibleState();

  const getNavBar = (): ReactElement => {
    const mode = window.posterEditor?.whiteboard?.mode;
    if (isEmbedded) {
      return <PosterEditorNavBar mode={PosterEditingNavbarMode.EMBEDDED} />;
    }
    switch (mode?.details.type) {
      case PosterModeType.REGEN:
        return <PosterEditorNavBar mode={PosterEditingNavbarMode.REGEN} />;
      default:
        return <PosterEditorNavBar />;
    }
  };

  const getEditorContainerClasses = (): string => {
    return `${styles.editorWithoutNav} ${styles.dottedBg} ${isMobileVariant ? styles.editorMobile : ''}`;
  };

  const shouldShowSmallMobileSeekbar = (): boolean => {
    return !isScaleAnimatingToFit && isMobileVariant && canvasHeight + POSTER_MOBILE_BIG_SEEK_BAR_HEIGHT > whiteboardContainerHeight;
  };

  const isPosterOverlappingWithBottomSheet = (): boolean => {
    return isMobileVariant && canvasHeight + 16 > whiteboardContainerHeight;
  };

  const getPosterWhiteboard = (): ReactElement => {
    return (
      <PosterEditorErrorBoundary>
        <PosterWhiteboard
          isMobileDisplay={isEditorMobileVariant()}
          postWhiteboardElement={<PostPosterElement />}
          postWhiteboardContainerElement={shouldShowSmallMobileSeekbar() && isPosterVideo ? <PosterMobileSmallSeekbar /> : null}
          isCenterAligned
          posterOpts={props.posterOpts}
        />
      </PosterEditorErrorBoundary>
    );
  };

  const getMobileVariant = (): ReactElement => {
    return (
      <div className={getEditorContainerClasses()}>
        <CursorOverlay />
        <PosterWhiteboard
          isMobileDisplay
          postWhiteboardElement={<PostPosterElement />}
          postWhiteboardContainerElement={shouldShowSmallMobileSeekbar() && isPosterVideo ? <PosterMobileSmallSeekbar /> : null}
          isCenterAligned
          posterOpts={props.posterOpts}
        />
        <Snackbar />
        <SnackbarMessageGrowl />
        <PostPosterModalSpacer />
        <React.StrictMode>
          <PosterEditorBottomBar isBottomSheetOverlapping={isPosterOverlappingWithBottomSheet()} />
        </React.StrictMode>
      </div>
    );
  };

  const getWebVariant = (): ReactElement => {
    return (
      <div className={getEditorContainerClasses()}>
        <div className={`flex flex-column-align-center _full-height _full-width ${!isSidebarSmall ? styles.maxWidth : ''}`}>
          <div className={`flex flex-row-align-center _full-width ${styles.transitionAccordingToTimeline} _full-height`}>
            <React.StrictMode>
              <PosterAddItemBar />
            </React.StrictMode>
            <CursorOverlay />
            {getPosterWhiteboard()}
          </div>
          <React.StrictMode>
            <PosterEditorWebBottomBar />
          </React.StrictMode>
        </div>
        <React.StrictMode>
          <PosterEditingSidePanel />
        </React.StrictMode>
      </div>
    );
  };

  return (
    <>
      <div className={`${styles.editor} ${className}`}>
        <React.StrictMode>{getNavBar()}</React.StrictMode>
        {isMobileVariant ? getMobileVariant() : getWebVariant()}
        <React.StrictMode>
          {isMobileVariant ? null : <PosterNavOpts />}
          {!isMobileVariant ? <SaveReminderNotification /> : null}
          <InitalPosterItemsLoadErrorNotification />
          {!isMobileVariant ? <AudioPlaylistItem /> : null}
        </React.StrictMode>
      </div>
      {!isMobileVariant ? <LoadingToast /> : null}
      {!isMobileVariant ? <MessageGrowl /> : null}
      {isMobileVariant ? <CanvasZoomPinchOnboarding /> : null}
      {isMobileVariant ? <TimelineMobileOnboarding /> : null}
    </>
  );
}
